import React from 'react';
import { Users } from 'lucide-react';
import {
  Input,
  Label,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
} from '@roger/ui-components';
import { formatUsPhoneEntry, isCompleteUsPhone } from '../../utils/intake';
import {
  MAX_LENGTHS,
  PHONE_ERROR_MESSAGE,
  type PatientInfoSectionProps,
} from './patientInfoForm';

// Emergency and secondary contacts: who to call when the patient's own number
// does not reach them, plus the representation flags. Contact phones follow
// the same completeness rule as the patient phone. Controlled by
// PatientInfoTab, which owns the form state and autosave.
export const PatientEmergencyContactsSection: React.FC<
  PatientInfoSectionProps
> = ({ form, updateField, canEditPatientInfo }) => {
  return (
    <Card>
      <CardHeader className='pb-4'>
        <div className='flex items-center gap-2'>
          <Users className='h-4 w-4 text-muted-foreground' />
          <h3 className='text-sm font-semibold'>Contacts</h3>
        </div>
      </CardHeader>
      <CardContent className='space-y-6'>
        <div className='space-y-3'>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Emergency Contact
          </p>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='emergency_contact'>Name</Label>
              <Input
                id='emergency_contact'
                value={form.emergency_contact}
                onChange={e => updateField('emergency_contact', e.target.value)}
                disabled={!canEditPatientInfo}
                maxLength={MAX_LENGTHS.contactName}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='emergency_contact_relationship'>
                Relationship
              </Label>
              <Input
                id='emergency_contact_relationship'
                value={form.emergency_contact_relationship}
                onChange={e =>
                  updateField('emergency_contact_relationship', e.target.value)
                }
                disabled={!canEditPatientInfo}
                placeholder='Daughter, spouse, neighbor'
                maxLength={MAX_LENGTHS.contactRelationship}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='emergency_phone'>Phone</Label>
              <Input
                id='emergency_phone'
                value={form.emergency_phone}
                onChange={e =>
                  updateField(
                    'emergency_phone',
                    formatUsPhoneEntry(e.target.value)
                  )
                }
                disabled={!canEditPatientInfo}
                inputSize='sm'
                aria-invalid={
                  !isCompleteUsPhone(form.emergency_phone) || undefined
                }
                aria-describedby={
                  !isCompleteUsPhone(form.emergency_phone)
                    ? 'emergency_phone-error'
                    : undefined
                }
              />
              {!isCompleteUsPhone(form.emergency_phone) && (
                <p
                  id='emergency_phone-error'
                  className='mt-1 text-xs text-destructive'
                  role='alert'
                >
                  {PHONE_ERROR_MESSAGE}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className='space-y-3 border-t pt-6'>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Secondary Contact
          </p>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='secondary_contact'>Name</Label>
              <Input
                id='secondary_contact'
                value={form.secondary_contact}
                onChange={e => updateField('secondary_contact', e.target.value)}
                disabled={!canEditPatientInfo}
                maxLength={MAX_LENGTHS.contactName}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='secondary_contact_relationship'>
                Relationship
              </Label>
              <Input
                id='secondary_contact_relationship'
                value={form.secondary_contact_relationship}
                onChange={e =>
                  updateField('secondary_contact_relationship', e.target.value)
                }
                disabled={!canEditPatientInfo}
                placeholder='Daughter, spouse, neighbor'
                maxLength={MAX_LENGTHS.contactRelationship}
                inputSize='sm'
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='secondary_phone'>Phone</Label>
              <Input
                id='secondary_phone'
                value={form.secondary_phone}
                onChange={e =>
                  updateField(
                    'secondary_phone',
                    formatUsPhoneEntry(e.target.value)
                  )
                }
                disabled={!canEditPatientInfo}
                inputSize='sm'
                aria-invalid={
                  !isCompleteUsPhone(form.secondary_phone) || undefined
                }
                aria-describedby={
                  !isCompleteUsPhone(form.secondary_phone)
                    ? 'secondary_phone-error'
                    : undefined
                }
              />
              {!isCompleteUsPhone(form.secondary_phone) && (
                <p
                  id='secondary_phone-error'
                  className='mt-1 text-xs text-destructive'
                  role='alert'
                >
                  {PHONE_ERROR_MESSAGE}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className='space-y-3 border-t pt-6'>
          <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
            Representation
          </p>
          <div className='flex flex-col gap-3 sm:flex-row sm:gap-8'>
            <div className='flex items-center gap-2'>
              <Checkbox
                id='needs_legal_representative'
                checked={form.needs_legal_representative}
                disabled={!canEditPatientInfo}
                onCheckedChange={checked =>
                  updateField('needs_legal_representative', checked === true)
                }
              />
              <Label
                htmlFor='needs_legal_representative'
                className='font-normal'
              >
                Needs legal representative
              </Label>
            </div>
            <div className='flex items-center gap-2'>
              <Checkbox
                id='has_poa'
                checked={form.has_poa}
                disabled={!canEditPatientInfo}
                onCheckedChange={checked =>
                  updateField('has_poa', checked === true)
                }
              />
              <Label htmlFor='has_poa' className='font-normal'>
                Has power of attorney
              </Label>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
